import React from 'react';
import PropTypes from 'prop-types';
import { debounce } from 'lodash';

import PageHeader from 'components/project/page_header';
import ReportPreview, { CARD_TYPES } from './report_preview';
import LeadsCard from './components/cards/leads';
import ActivityCard from './components/cards/activity';
import WebsiteCard from './components/cards/website';
import EmailCard from './components/cards/email';
import SyndicationCard from './components/cards/syndication';
import CustomCard from './components/cards/custom';
import ShareModal from './components/share_modal';
import ClientReportFooter from './client_report_customization_banner';
import { uploadClientReportCardImage } from './api';

import './css/report_tab.scss';

export default class ReportTab extends React.Component {
  static propTypes = {
    addCard: PropTypes.func,
    batchUpdateCards: PropTypes.func,
    cards: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        cardType: PropTypes.oneOf(CARD_TYPES).isRequired,
        content: PropTypes.string,
        title: PropTypes.string,
        subtitle: PropTypes.string,
        visibleToClient: PropTypes.bool.isRequired
      })
    ).isRequired,
    fetchAnalyticsMetrics: PropTypes.func,
    fetchFastMetrics: PropTypes.func,
    fetchingLeads: PropTypes.bool,
    fetchLeads: PropTypes.func,
    fetchMetrics: PropTypes.func.isRequired,
    hideLastActivity: PropTypes.bool,
    leads: PropTypes.array,
    leadStatuses: PropTypes.array,
    preview: PropTypes.bool,
    project: PropTypes.any, // When we convert all users over to the new one we can delete this and the rest of it
    projectId: PropTypes.number.isRequired,
    removeCard: PropTypes.func,
    resendShareableToken: PropTypes.func,
    updateCard: PropTypes.func,
    updateLeadNote: PropTypes.func
  }

  state = {
    metrics: { loadingMetrics: true, loadingAnalytics: true },
    leads: this.props.leads,
    savedNoteForLeadId: null,
    renderNotificationTimeout: null,
    showShareModal: false
  }

  componentDidMount() {
    if (this.props.fetchLeads) this.props.fetchLeads(this.props.projectId);
    if (this.state.metrics.loadingMetrics) this.getFastMetrics();
    if (this.state.metrics.loadingAnalytics) this.getAnalyticsMetrics();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.fetchingLeads && !this.props.fetchingLeads) this.setState({ leads: this.props.leads });
  }

  getFastMetrics = () => {
    return this.props.fetchFastMetrics().then((fastMetrics) => {
      this.setState((prevState) => {
        return { metrics: { ...prevState.metrics, ...fastMetrics, loadingMetrics: false } };
      });
    });
  }

  getAnalyticsMetrics = () => {
    return this.props.fetchAnalyticsMetrics().then((analyticsMetrics) => {
      this.setState((prevState) => {
        return { metrics: { ...prevState.metrics, ...analyticsMetrics, loadingAnalytics: false } };
      });
    });
  }

  debouncedUpdateLeadNote = debounce(
    (projectId, updatedLead) => {
      this.props.updateLeadNote(projectId, updatedLead);
      this.setState((prevState) => {
        clearTimeout(prevState.renderNotificationTimeout);
        const timeout = setTimeout(() => this.setState({ savedNoteForLeadId: null }), 500);

        return { savedNoteForLeadId: updatedLead.id, renderNotificationTimeout: timeout };
      });
    },
    500
  )

  allCardsVisible = () => this.props.cards.every(c => c.visibleToClient)

  someCardsVisible = () => !this.allCardsVisible() && this.props.cards.some(c => c.visibleToClient)

  showShareModal = () => {
    this.setState({ showShareModal: true });
  }

  hideShareModal = () => {
    this.setState({ showShareModal: false });
  }

  updateNote = (leadId, body) => {
    const { projectId } = this.props;

    return this.setState((prevState) => {
      let updatedLead;
      const leads = prevState.leads.map((lead) => {
        const newLead = { ...lead, noteToClient: { ...lead.noteToClient } };

        if (lead.id == leadId) {
          newLead.noteToClient.body = body;
          updatedLead = { ...newLead };
        }

        return newLead;
      });

      this.debouncedUpdateLeadNote(projectId, updatedLead);

      return { leads };
    });
  }

  clientView = () => !this.props.addCard

  setActiveCard = (activeCard, e) => {
    if (e) e.preventDefault();
    this.setState({ activeCard });
  }

  updateCardSortOrder = cards => (
    this.props.batchUpdateCards(cards.map((card, sortOrder) => ({ ...card, sortOrder })))
  )

  updateCardVisibility = (id, visibleToClient) => {
    this.props.updateCard({ id, visibleToClient });
  }

  updateAllCardsVisibilty = (e) => {
    const { checked } = e.target;

    this.props.batchUpdateCards(this.props.cards.map(card => ({ ...card, visibleToClient: checked })));
  }

  uploadImageToCard = (id, imageFile) => {
    return uploadClientReportCardImage(this.props.projectId, id, imageFile);
  }

  renderExpandedCard = (card) => {
    const { activeCard, leads, metrics } = this.state;
    const { addCard, updateCard, leadStatuses, removeCard, hideLastActivity } = this.props;

    const baseProps = {
      card,
      fullScreen: true,
      hideLastActivity: hideLastActivity,
      key: card.id,
      name: `${card.cardType}ReportPanel`,
      onSetAsActive: this.setActiveCard
    };

    switch (card.cardType) {
    case 'activity':
      return <ActivityCard {...baseProps} metadata={metrics.activityMetadata} />;
    case 'custom': {
      let saveFunc = null;

      if (!this.clientView()) {
        saveFunc = (activeCard && activeCard.id) ? updateCard : addCard;
      }

      return (
        <CustomCard
          {...baseProps}
          readOnly={this.clientView()}
          onRemove={removeCard}
          onSave={saveFunc}
          onUploadImage={file => this.uploadImageToCard(card.id, file)}
        />
      );
    }
    case 'email':
      return <EmailCard {...baseProps} {...metrics} />;
    case 'leads':
      return (
        <LeadsCard
          {...baseProps}
          editable={!this.clientView()}
          leads={leads}
          leadStatuses={leadStatuses}
          loading={this.props.fetchingLeads}
          savedNoteForLeadId={this.state.savedNoteForLeadId}
          onUpdateNote={this.updateNote}
        />
      );
    case 'website':
      return (
        <WebsiteCard
          {...baseProps}
          fetchMetrics={this.props.fetchMetrics}
          metrics={metrics}
        />
      );
    case 'syndication' :
      return (
        <SyndicationCard {...baseProps} syndicationPartners={metrics.syndicationPartners} />
      );
    }
  }

  renderHeader() {
    if (this.state.activeCard || this.clientView()) return;

    return <PageHeader actions={this.renderActions()} title="Client Report" />;
  }

  renderActions() {
    if (this.clientView()) return;

    return (
      <PageHeader.Actions>
        <div className="text-muted text-nowrap mr-3">
          <div className="d-inline-block">All Cards Visible</div>
          <input
            checked={this.allCardsVisible()}
            className="ml-2 my-0 vertical-center"
            name="toggleAllCardsVisiblity"
            ref={el => el && (el.indeterminate = this.someCardsVisible())}
            type="checkbox"
            onChange={this.updateAllCardsVisibilty}
          />
        </div>
        <div className="d-inline-block">
          <a className="btn btn-dark btn-sm right-buffer-sm"
            href={`/projects/${this.props.projectId}/client_report/preview`}
            rel="noopener noreferrer"
            target="_blank"
          >
            Preview
          </a>
          <button
            className="btn btn-primary btn-sm"
            name="open-share-modal"
            onClick={this.showShareModal}
          >
            Share
          </button>
        </div>
      </PageHeader.Actions>
    );
  }

  renderBody() {
    if (this.state.activeCard) return this.renderExpandedCard(this.state.activeCard);

    return (
      <ReportPreview
        addCard={this.props.addCard}
        cards={this.props.cards}
        fetchingLeads={this.props.fetchingLeads}
        leads={this.state.leads}
        leadStatuses={this.props.leadStatuses}
        metrics={this.state.metrics}
        projectId={this.props.projectId}
        setActiveCard={this.setActiveCard}
        updateCard={this.props.updateCard}
        updateCardSortOrder={this.updateCardSortOrder}
        onUpdateCardVisibility={this.updateCardVisibility}
      />
    );
  }

  render() {
    return (
      <div>
        {this.renderHeader()}
        {this.renderBody()}
        {!this.clientView() &&
          <ShareModal
            projectId={this.props.projectId}
            resendToken={this.props.resendShareableToken}
            show={this.state.showShareModal}
            onHide={this.hideShareModal}
          />
        }
        { !this.props.preview && this.props.project.showClientReportToggle && <ClientReportFooter project={this.props.project} /> }
      </div>
    );
  }
}
